<script lang="ts">
export default {
  name: 'KkItemCounter',
}
</script>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { KkBox, KkTooltip, KkValidationError } from '../../../../components/base'
import { KkMultiCounterBar } from '../../../../components/ui'
import type { FoodMenu, Counter } from 'src/biz_spa/domains/foodMenu/types'
import { camelCase } from '../../../../modules/string_converter'
import { menuCategories, categoryItemMaxLimits, servicesWithItemLimits } from 'src/biz_spa/domains/foodMenu/consts'
import type { MenuCategory, ServiceType } from 'src/biz_spa/domains/foodMenu/consts'

import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const showCategoryCounterTooltip = ref(false)

interface Props {
  foodMenu: FoodMenu,
  selectedServiceTypes: ServiceType[]
}

const props = defineProps<Props>()

const counters = computed<Counter[]>(() => {
  return menuCategories.map((category: MenuCategory) => {
    const currentCount = strictestServiceData.value.service ? calculateItemCount(strictestServiceData.value.service, category) : 0

    return {
      label: t(`pages.CompaniesFoodMenus.edit.category.${category}`),
      currentCount: currentCount,
      denominator: strictestServiceData.value.limit ?? 0,
    }
  })
})

// 選択されているうち、最も個数の制約が厳しいサービスのカウンター上限を取得する
const strictestServiceData = computed<
  {
    service: ServiceType | null;
    limit: number | null;
  }
>(() => {
  let minLimit = Number.MAX_VALUE
  let serviceWithMinLimit: ServiceType | null = null

  for (const service of props.selectedServiceTypes) {
    const limit = categoryItemMaxLimits[service]
    if (limit === undefined || limit === null) continue
    if (limit < minLimit) {
      minLimit = limit
      serviceWithMinLimit = service
    }
  }

  return {
    service: serviceWithMinLimit,
    limit: minLimit === Number.MAX_VALUE ? null : minLimit,
  }
})

// アイテム上限のあるサービスだけ、ツールチップに表示する
const displayedServicesAtTooltip = computed<ServiceType[] | []>(() => {
  return props.selectedServiceTypes.filter(service =>
    servicesWithItemLimits.includes(service)
  )
})

const calculateItemCount = (service: ServiceType, category: MenuCategory): number => {
  const targetSections = props.foodMenu[category]?.sections ?? []

  const itemCount = targetSections.map(section => section.items.length).reduce((a, b) => a + b, 0)
  const sectionLabelCount = targetSections.filter(s => s.label).length
  const sectionDescriptionCount = targetSections.filter(s => s.description).length

  let currentCount = itemCount + sectionLabelCount
  // ぐるなびの場合はセクションの説明もカウントする
  if (service === 'gnavi') currentCount += sectionDescriptionCount

  return currentCount
}

// 上限を超えたサービス名
const limitExceededService = computed<ServiceType | null>(() => {
  return props.selectedServiceTypes.find(service => {
    const limit = categoryItemMaxLimits[service]
    if (limit === undefined || limit === null) return ''
    return menuCategories.some(category => {
      const currentCount = calculateItemCount(service, category)
      return currentCount > limit
    })
  }) ?? null
})

const errorText = computed<string> (() => {
  // 上限を超えたサービスがあるかどうか
  if (limitExceededService.value) {
    // 上限を超えたサービスが表示されているカウンターのものと同じ場合
    if (limitExceededService.value === strictestServiceData.value.service) {
      return t('pages.CompaniesFoodMenus.edit.categoryCounter.countLimitError')

    }
    // 上限を超えたサービスがカウンターに表示されていない場合
    return t('pages.CompaniesFoodMenus.edit.categoryCounter.serviceCountLimitError', {
      service: t(`labels.sites.${camelCase(limitExceededService.value ?? '')}`)
    })
  }
  return ''
})
</script>
<template>
  <KkBox border class="categoryCounter">
    <template #content>
      <KkTooltip
        :label="t('pages.CompaniesFoodMenus.edit.categoryCounter.tooltip.label')"
        bottom="30px"
        left="-240px"
        width="400px"
        class="categoryCounter__tooltip"
        :is-visible="showCategoryCounterTooltip"
        @showTooltip="showCategoryCounterTooltip = true"
        @hideTooltip="showCategoryCounterTooltip = false"
      >
        <p class="whiteSpacePreWrap">
          {{ t('pages.CompaniesFoodMenus.edit.categoryCounter.tooltip.content', { 'service': t(`labels.sites.${camelCase(strictestServiceData.service ?? '')}`) }) }}<br>
          <span v-for="service in displayedServicesAtTooltip" :key="service">
            <br><span class="categoryCounter__tooltip__service">{{ t(`labels.sites.${camelCase(service)}`) }}</span><br>
            {{ t(`pages.CompaniesFoodMenus.edit.categoryCounter.tooltip.${service}`) }}<br>
          </span>
        </p>
      </KkTooltip>
      <KkMultiCounterBar :counters="counters" class="multiCounterBar" />
      <KkValidationError
        id="counterLimitError"
        :text="errorText"
      />
    </template>
  </KkBox>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.categoryCounter {
  margin-top: 32px;

  &__title {
    margin-bottom: 16px;
  }

  &__tooltip {
    &__service {
      font-weight: bold;
    }
  }
}
.whiteSpacePreWrap {
  white-space: pre-wrap;
}
.multiCounterBar {
  margin-top: 16px;
}
</style>
